import { createReducer } from '@reduxjs/toolkit';

import {
  discardQuery,
  filterQueryChanged,
  filterQueryResetRequested,
  loadMore,
  removeMaxCountByField,
  replaceRequest,
  resetMaxCountByField,
  resetRequest,
  retryRequest,
  setNextPage,
  setPageNumber,
  setPageSize,
  setPrevPage,
  setSort,
} from 'Core/actions/request';
import { setResponse } from 'Core/actions/response';
import { getRequestMeta } from 'Core/reducers/common';
import { normalizePageSize } from 'Models/pagination';
import requestConfig from 'Models/uiConfig/requestConfig';

const { defaultSort, defaultPageSize, maxCountByField } = requestConfig;
const defaultPagination = normalizePageSize(defaultPageSize);

function removeMaxCountEntry(values: Record<string, number>, fieldToRemove: string): Record<string, number> {
  const nextValues: Record<string, number> = {};

  for (const [field, value] of Object.entries(values)) {
    if (field !== fieldToRemove) {
      nextValues[field] = value;
    }
  }

  return nextValues;
}

const initStates = {
  action: 'set',
  catalog: '',
  filterQuery: '',
  maxCountByField,
  pageNumber: 0,
  pageSize: defaultPagination.pageSize,
  pageSizeMode: defaultPagination.pageSizeMode,
  query: '',
  sort: defaultSort,
};

export default {
  action: createReducer(initStates.action, (builder) =>
    builder
      .addCase(loadMore, () => 'add')
      .addCase(retryRequest, (state, { payload }) =>
        // If retrying with a nested action present, reset to 'set' (mirrors previous runtime behavior);
        // if retrying without one (bare retry), preserve the current mode.
        payload.action ? initStates.action : state,
      )
      .addDefaultCase(() => initStates.action),
  ),
  catalog: createReducer(initStates.catalog, () => {}),
  filterQuery: createReducer(initStates.filterQuery, (builder) =>
    builder
      .addCase(filterQueryChanged, (_, { payload }) => payload)
      .addCase(filterQueryResetRequested, () => initStates.filterQuery)
      .addCase(resetRequest, () => initStates.filterQuery)
      .addCase(replaceRequest, (_, { payload }) => payload.filterQuery ?? ''),
  ),
  maxCountByField: createReducer(initStates.maxCountByField, (builder) =>
    builder
      .addCase(removeMaxCountByField, (state, { payload: { field } }) => removeMaxCountEntry(state, field))
      .addCase(resetMaxCountByField, (state, { payload: { field } }) => ({
        ...state,
        [field]: maxCountByField[field],
      })),
  ),
  pageNumber: createReducer(initStates.pageNumber, (builder) =>
    builder
      .addCase(loadMore, (state) => state + 1)
      .addCase(replaceRequest, (_, { payload }) => payload.pageNumber ?? initStates.pageNumber)
      .addCase(resetRequest, () => initStates.pageNumber)
      .addCase(retryRequest, (state) => state)
      .addCase(setNextPage, (state) => state + 1)
      .addCase(setPageNumber, (_, { payload }) => payload.pageNumber)
      .addCase(setPrevPage, (state) => state - 1)
      .addDefaultCase((state, action) => {
        const { sendRequest } = getRequestMeta(action);
        return sendRequest ? initStates.pageNumber : state;
      }),
  ),
  pageSize: createReducer(initStates.pageSize, (builder) =>
    builder
      .addCase(replaceRequest, (state, { payload }) =>
        payload.pageSize === undefined ? state : normalizePageSize(payload.pageSize).pageSize,
      )
      .addCase(setPageSize, (_, { payload }) => normalizePageSize(payload.pageSize).pageSize),
  ),
  pageSizeMode: createReducer(initStates.pageSizeMode, (builder) =>
    builder
      .addCase(replaceRequest, (state, { payload }) => {
        if (payload.pageSizeMode === 'all') {
          return 'all';
        }

        if (payload.pageSizeMode === null) {
          return null;
        }

        return payload.pageSize === undefined ? state : normalizePageSize(payload.pageSize).pageSizeMode;
      })
      .addCase(setPageSize, (_, { payload }) => normalizePageSize(payload.pageSize).pageSizeMode),
  ),
  query: createReducer(initStates.query, (builder) =>
    builder
      .addCase(discardQuery, () => initStates.query)
      .addCase(replaceRequest, (_, { payload }) => payload.query ?? initStates.query)
      .addCase(setResponse, (state, { payload }) => payload.Query ?? state)
      .addCase(resetRequest, () => initStates.query),
  ),
  sort: createReducer(initStates.sort, (builder) =>
    builder
      .addCase(replaceRequest, (state, { payload }) => payload.sort ?? state)
      .addCase(resetRequest, () => initStates.sort)
      .addCase(setSort, (_, { payload }) => payload.sort),
  ),
};
